import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Shield, UserPlus, Check, Users } from "lucide-react";
import Portrait from "@/components/Portrait";
import GuildLogo from "@/components/GuildLogo";

const RARITY = {
  common: "#B0ADA5", uncommon: "#6E8B3D", rare: "#4A7BC7",
  epic: "#8A2BE2", legendary: "#E5C34A", mythic: "#E33333",
};

/**
 * Public player profile modal. Shows name, class, level, active title,
 * guild, equipped gear (all slots), stats, and "Add Friend" button.
 */
export default function PlayerProfile({ userId, onClose }) {
  const [prof, setProf] = useState(null);
  const [friendBusy, setFriendBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    // Reset immediately so a stale profile from the previous click cannot
    // linger on screen while the new fetch is in flight (was the source
    // of the "wrong player shown" bug when switching quickly).
    setProf(null);
    if (!userId) return;
    api.profile(userId)
      .then(p => { if (alive) setProf(p); })
      .catch(() => { if (alive) setProf({ error: true }); });
    return () => { alive = false; };
  }, [userId]);

  const addFriend = async () => {
    if (friendBusy) return;
    setFriendBusy(true);
    try {
      const res = await api.friendRequest(userId);
      toast.success(res.status === "accepted" ? "You are now friends." : "Friend request sent.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Request failed");
    } finally { setFriendBusy(false); }
  };

  if (!prof) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center px-3"
      style={{ background: "rgba(5,5,5,0.86)", backdropFilter: "blur(2px)" }}
      onClick={onClose}
      data-testid="player-profile"
    >
      <div
        className="panel p-4 max-w-sm w-full space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        {prof.error && (
          <div className="text-center text-[#8A0303] italic text-sm">Profile unavailable.</div>
        )}
        {!prof.error && (
          <>
            <div className="flex items-start gap-3">
              <Portrait name={prof.name} cls={prof.class} appearance={prof.appearance} active_title={prof.active_title} size={72} />
              <div className="flex-1 min-w-0">
                <div className="font-heading text-lg tracking-widest uppercase text-[#E2DFD8] flex items-center gap-1">
                  {prof.name}
                  {prof.is_admin && (
                    <span className="ml-1 px-1 border border-[#B89C3A] text-[#E5C34A] font-mono text-[8px]">
                      ADMIN
                    </span>
                  )}
                </div>
                <div className="font-mono text-[10px] tracking-widest uppercase text-[#8c8c8c]">
                  Lv {prof.level} · {prof.class}
                </div>
                {prof.active_title && (
                  <div className="font-body italic text-[11px] text-[#B89C3A] mt-1">
                    «{prof.active_title}»
                  </div>
                )}
                {prof.guild && (
                  <div className="flex items-center gap-1 mt-1">
                    <GuildLogo logo={prof.guild.logo || "raven"} size={16} color="#B89C3A" />
                    <span className="font-mono text-[9px] tracking-widest uppercase text-[#B89C3A]">
                      [{prof.guild.tag}] {prof.guild.name}
                    </span>
                  </div>
                )}
              </div>
              <button
                data-testid="player-profile-close"
                onClick={onClose}
                className="text-[#8c8c8c] hover:text-[#E2DFD8]"
              >
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-3 gap-1 border-t border-[#262626] pt-2">
              {Object.entries(prof.stats || {}).map(([k, v]) => (
                <div key={k} className="font-mono text-[10px] tracking-widest uppercase text-[#B0ADA5] text-center">
                  <span className="text-[#666]">{k.slice(0, 3)}</span>{" "}
                  <span className="text-[#E2DFD8]">{v}</span>
                </div>
              ))}
            </div>

            {/* Equipment */}
            <div className="border-t border-[#262626] pt-2">
              <div className="font-heading text-[10px] tracking-widest uppercase text-[#8c8c8c] mb-1">
                Equipped
              </div>
              <div className="grid grid-cols-1 gap-1">
                {["weapon", "armor", "trinket"].map(slot => {
                  const it = prof.equipped?.[slot];
                  const tone = it ? (RARITY[it.rarity] || "#B0ADA5") : "#333";
                  return (
                    <div
                      key={slot}
                      className="flex items-center gap-2 p-1 border"
                      style={{ borderColor: tone, background: it ? `${tone}10` : "transparent" }}
                      data-testid={`profile-slot-${slot}`}
                    >
                      <Shield className="w-3 h-3" strokeWidth={1.5} style={{ color: tone }} />
                      <span className="font-mono text-[9px] uppercase tracking-widest text-[#666] w-14">
                        {slot}
                      </span>
                      <span className="text-xs flex-1 truncate" style={{ color: it ? tone : "#555" }}>
                        {it?.name || "— empty —"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Titles */}
            {prof.titles?.length > 0 && (
              <div className="border-t border-[#262626] pt-2">
                <div className="font-heading text-[10px] tracking-widest uppercase text-[#8c8c8c] mb-1 flex items-center gap-1">
                  <Users className="w-3 h-3" strokeWidth={1.5} />
                  Titles ({prof.titles.length})
                </div>
                <div className="flex flex-wrap gap-1">
                  {prof.titles.slice(0, 12).map(t => (
                    <span
                      key={t}
                      className="font-mono text-[9px] tracking-widest uppercase px-1 border"
                      style={{
                        borderColor: t === prof.active_title ? "#E5C34A" : "#333",
                        color: t === prof.active_title ? "#E5C34A" : "#B0ADA5",
                      }}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            {!prof.is_self && (
              <button
                data-testid="player-profile-add-friend"
                disabled={friendBusy}
                onClick={addFriend}
                className="w-full py-2 border border-[#6E8B3D] text-[#6E8B3D] font-heading text-[10px] tracking-widest uppercase flex items-center justify-center gap-2 disabled:opacity-40 hover:bg-[#6E8B3D]/10"
              >
                <UserPlus className="w-3 h-3" strokeWidth={1.5} /> Add as Friend
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
